import React, { useEffect, useRef, useState } from 'react';
import BottomSheet from '../components/BottomSheet';
import { setDefaultAddress } from './api';

// Alamat tersimpan pelanggan:
// - AddressPickerSheet: popup "Pilih alamat" di checkout (BottomSheet di HP, dialog di laptop) berisi kartu
//   alamat dengan tombol "Pilih alamat", tautan kecil "Jadikan default"/"Hapus" (dengan konfirmasi), dan
//   tombol "Tambah alamat baru".
// - DefaultAddressOffer: popup sekali setelah pesanan dibuat, "Jadikan alamat ini sebagai default?", hanya
//   bila server melaporkan pelanggan belum punya alamat default. Penanda disimpan di sessionStorage per nomor
//   pesanan oleh checkout dan DIHAPUS saat dibaca, sehingga tidak muncul lagi saat halaman dimuat ulang.

const OFFER_PREFIX = 'mihan.defaultAddressOffer.';

export const rememberDefaultOffer = (orderNo, addressId) => {
  try {
    window.sessionStorage.setItem(OFFER_PREFIX + orderNo, String(addressId));
  } catch {
    /* penyimpanan diblokir: popup tidak ditawarkan */
  }
};

// Ambil lalu hapus penanda (sekali pakai). null bila tidak ada.
export const takeDefaultOffer = (orderNo) => {
  try {
    const k = OFFER_PREFIX + orderNo;
    const v = window.sessionStorage.getItem(k);
    window.sessionStorage.removeItem(k);
    const id = Number(v);
    return v && Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

// Wilayah singkat: "Kel. X, Kec. Y, Kab/Kota, Provinsi" (bagian kosong dilewati).
export const regionLine = (a) => {
  const r = a?.region;
  if (!r) return a?.city || '';
  return [r.village?.name, r.district?.name ? `Kec. ${r.district.name}` : '', r.regency?.name || a.city, r.province?.name].filter(Boolean).join(', ');
};

const CloseButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Tutup"
    className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
  >
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  </button>
);

const DefaultBadge = () => (
  <span className="ml-2 inline-block rounded-full bg-purple-100 px-2 py-0.5 align-middle text-xs font-semibold text-purple-800">Default</span>
);

const AddressCard = ({ a, busy, onPick, onDelete, onMakeDefault }) => {
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="rounded-lg border border-gray-200 p-3" data-testid="address-card">
      <p className="font-semibold text-gray-900 break-words">
        {a.recipientName}
        {a.isDefault && <DefaultBadge />}
      </p>
      <p className="text-sm text-gray-600">{a.recipientPhone}</p>
      <p className="mt-1 text-sm text-gray-700 break-words line-clamp-2">{a.address}</p>
      <p className="text-xs text-gray-500 break-words">
        {regionLine(a)}
        {a.postalCode ? ` ${a.postalCode}` : ''}
      </p>
      {!a.regionComplete && <p className="mt-1 text-xs text-amber-800">Wilayah belum lengkap: lengkapi provinsi sampai kelurahan setelah memilih.</p>}
      {confirming ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" role="group" aria-label="Konfirmasi hapus alamat">
          <span className="text-gray-800">Hapus alamat ini?</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(a)}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            Ya, hapus
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700"
          >
            Batal
          </button>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick(a)}
            className="rounded-lg bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
          >
            Pilih alamat
          </button>
          {!a.isDefault && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onMakeDefault(a)}
              className="text-xs font-medium text-gray-500 underline hover:text-gray-700"
            >
              Jadikan default
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirming(true)}
            className="text-xs font-medium text-gray-500 underline hover:text-red-700"
          >
            Hapus
          </button>
        </div>
      )}
    </li>
  );
};

// Header & tombol "Tambah alamat baru" MENETAP; hanya daftar alamat yang ter-scroll (maks. 85vh).
export const AddressPickerSheet = ({ open, onClose, addresses, busyId, message, onPick, onNew, onDelete, onMakeDefault }) => (
  <BottomSheet
    open={open}
    onClose={onClose}
    labelledBy="address-picker-title"
    testId="address-picker"
    header={
      <>
        <div className="flex items-start justify-between gap-3">
          <h2 id="address-picker-title" className="text-lg font-bold text-gray-900">
            Pilih alamat
          </h2>
          <CloseButton onClick={onClose} />
        </div>
        {message && (
          <p role="status" className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="address-picker-message">
            {message}
          </p>
        )}
      </>
    }
    footer={
      <button
        type="button"
        onClick={onNew}
        className="min-h-[44px] w-full rounded-lg border border-purple-700 bg-white px-4 py-2.5 text-sm font-semibold text-purple-700 hover:bg-purple-50"
      >
        Tambah alamat baru
      </button>
    }
  >
    <ul className="mt-1 space-y-3">
      {addresses.map((a) => (
        <AddressCard key={a.id} a={a} busy={busyId === a.id} onPick={onPick} onDelete={onDelete} onMakeDefault={onMakeDefault} />
      ))}
    </ul>
  </BottomSheet>
);

// Mode ajakan di checkout (punya alamat tersimpan, belum ada alamat dipilih): hanya dua tombol, tanpa form.
export const AddressPrompt = ({ onPick, onNew }) => (
  <div className="rounded-xl border-2 border-dashed border-purple-200 bg-purple-50/40 p-4" data-testid="address-prompt">
    <p className="text-sm text-gray-700">Pilih atau tambah alamat pengiriman terlebih dahulu.</p>
    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
      <button
        type="button"
        onClick={onPick}
        className="min-h-[44px] rounded-lg bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800"
      >
        Pilih alamat
      </button>
      <button
        type="button"
        onClick={onNew}
        className="min-h-[44px] rounded-lg border border-purple-700 bg-white px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
      >
        Tambah alamat baru
      </button>
    </div>
  </div>
);

// Card alamat terpilih di checkout (pengganti form isian saat alamat tersimpan lengkap dipakai).
export const SelectedAddressCard = ({ a, onChange, onNew, onEdit }) => (
  <div className="rounded-xl border-2 border-purple-200 bg-purple-50/40 p-4" data-testid="selected-address-card">
    <div className="flex items-start justify-between gap-3">
      <p className="min-w-0 font-semibold text-gray-900 break-words">
        {a.recipientName}
        {a.isDefault && <DefaultBadge />}
      </p>
      <button type="button" onClick={onEdit} className="shrink-0 text-sm font-medium text-purple-700 underline hover:text-purple-900">
        Ubah
      </button>
    </div>
    <p className="text-sm text-gray-600">{a.recipientPhone}</p>
    <p className="mt-1 text-sm text-gray-800 break-words">{a.address}</p>
    <p className="text-sm text-gray-600 break-words" data-testid="selected-address-region">
      {regionLine(a)}
      {a.postalCode ? ` ${a.postalCode}` : ''}
    </p>
    <p className="mt-3 text-xs text-gray-600">Pesanan akan dikirim ke alamat ini. Ganti alamat atau tambah alamat baru bila perlu.</p>
    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
      <button
        type="button"
        onClick={onChange}
        className="min-h-[44px] rounded-lg bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800"
      >
        Ganti alamat
      </button>
      <button
        type="button"
        onClick={onNew}
        className="min-h-[44px] rounded-lg border border-purple-700 bg-white px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
      >
        Tambah alamat baru
      </button>
    </div>
  </div>
);

// Popup setelah pesanan dibuat. recipient = order.recipient (salinan alamat di pesanan).
export const DefaultAddressOffer = ({ orderNo, recipient }) => {
  const [addressId, setAddressId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [toast, setToast] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const id = takeDefaultOffer(orderNo);
    if (id) {
      setAddressId(id);
      setOpen(true);
    }
  }, [orderNo]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const later = () => setOpen(false);
  const accept = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await setDefaultAddress(addressId);
      if (!mounted.current) return;
      setOpen(false);
      setToast('Alamat dijadikan default.');
    } catch {
      if (mounted.current) setFailed(true);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <>
      <BottomSheet open={open && !!recipient} onClose={later} labelledBy="default-address-title" testId="default-address-sheet">
        <div className="flex items-start justify-between gap-3">
          <h2 id="default-address-title" className="text-lg font-bold text-gray-900">
            Jadikan alamat ini sebagai default?
          </h2>
          <CloseButton onClick={later} />
        </div>
        <p className="mt-1 text-sm text-gray-500">Alamat default otomatis terisi saat checkout berikutnya.</p>
        {recipient && (
          <div className="mt-3 rounded-lg border border-gray-200 p-3 text-sm" data-testid="default-address-summary">
            <p className="font-semibold text-gray-900 break-words">{recipient.name}</p>
            <p className="text-gray-600">{recipient.phone}</p>
            <p className="mt-1 text-gray-700 break-words">{recipient.fullAddress || recipient.address}</p>
          </div>
        )}
        {failed && (
          <p role="status" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="default-address-error">
            Gagal menyimpan alamat default. Periksa koneksi lalu coba lagi.
          </p>
        )}
        <button
          type="button"
          onClick={accept}
          disabled={busy}
          className="mt-4 w-full rounded-lg bg-purple-700 px-4 py-3 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
        >
          {busy ? 'Menyimpan...' : failed ? 'Coba lagi' : 'Ya, jadikan default'}
        </button>
        <button
          type="button"
          onClick={later}
          className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
        >
          Nanti saja
        </button>
      </BottomSheet>
      {toast && (
        <div
          role="status"
          data-testid="default-address-toast"
          className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-sm rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-lg"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {toast}
        </div>
      )}
    </>
  );
};
